'use client';

import { Select as BaseSelect } from '@base-ui-components/react/select';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '../../utils/cn';
import { useInheritedColorScheme } from '../../utils/use-inherited-color-scheme';
import './select.css';

interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string | null) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  children: React.ReactNode;
}

function Select({ children, onValueChange, ...props }: SelectProps) {
  const rootProps = {
    ...props,
    onValueChange: onValueChange
      ? (val: string | null) => onValueChange(val)
      : undefined,
  } as React.ComponentProps<typeof BaseSelect.Root>;
  return <BaseSelect.Root {...rootProps}>{children}</BaseSelect.Root>;
}

interface SelectTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

function SelectTrigger({ className, children, ...props }: SelectTriggerProps) {
  return (
    <BaseSelect.Trigger
      className={cn(
        'border-input bg-background flex h-9 w-full items-center justify-between rounded-md border px-3 py-2 text-sm',
        'placeholder:text-muted-foreground',
        'focus:ring-ring focus:ring-offset-background focus:ring-2 focus:ring-offset-2 focus:outline-none',
        'disabled:cursor-not-allowed disabled:opacity-50',
        '[&>span]:line-clamp-1',
        className
      )}
      {...(props as React.ComponentProps<typeof BaseSelect.Trigger>)}
    >
      {children}
      <BaseSelect.Icon className="flex h-4 w-4 shrink-0 items-center justify-center opacity-50">
        <ChevronDown className="h-4 w-4" />
      </BaseSelect.Icon>
    </BaseSelect.Trigger>
  );
}

interface SelectValueProps {
  placeholder?: string;
  className?: string;
  children?: React.ReactNode | ((value: unknown) => React.ReactNode);
}

function SelectValue({
  className,
  placeholder,
  children,
  ...props
}: SelectValueProps) {
  return (
    <BaseSelect.Value className={cn(className)} {...props}>
      {placeholder && !children
        ? (value: unknown) =>
            value == null ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              String(value)
            )
        : children}
    </BaseSelect.Value>
  );
}

interface SelectContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
}

function SelectContent({
  className,
  side = 'bottom',
  sideOffset = 4,
  align = 'start',
  children,
  ...props
}: SelectContentProps) {
  const { probeRef, themeAttrs } = useInheritedColorScheme();

  return (
    <>
      <span ref={probeRef} style={{ display: 'none' }} />
      <BaseSelect.Portal>
        <BaseSelect.Positioner
          side={side}
          sideOffset={sideOffset}
          align={align}
        >
          <BaseSelect.Popup
            {...themeAttrs}
            className={cn(
              'select-content bg-popover text-popover-foreground z-50 min-w-[8rem] overflow-hidden rounded-md border p-1 shadow-md',
              'transition-all duration-150',
              'data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
              'data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
              className
            )}
            {...(props as React.ComponentProps<typeof BaseSelect.Popup>)}
          >
            {children}
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </>
  );
}

interface SelectGroupProps extends React.HTMLAttributes<HTMLDivElement> {}

function SelectGroup({ className, ...props }: SelectGroupProps) {
  return (
    <BaseSelect.Group
      className={cn(className)}
      {...(props as React.ComponentProps<typeof BaseSelect.Group>)}
    />
  );
}

interface SelectLabelProps extends React.HTMLAttributes<HTMLDivElement> {}

function SelectLabel({ className, ...props }: SelectLabelProps) {
  return (
    <BaseSelect.GroupLabel
      className={cn('px-2 py-1.5 text-sm font-semibold', className)}
      {...(props as React.ComponentProps<typeof BaseSelect.GroupLabel>)}
    />
  );
}

interface SelectItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  disabled?: boolean;
}

function SelectItem({ className, children, ...props }: SelectItemProps) {
  return (
    <BaseSelect.Item
      className={cn(
        'relative flex w-full cursor-default items-center rounded-sm py-1.5 pr-2 pl-8 text-sm outline-none select-none',
        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className
      )}
      {...(props as React.ComponentProps<typeof BaseSelect.Item>)}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <BaseSelect.ItemIndicator>
          <Check className="h-4 w-4" />
        </BaseSelect.ItemIndicator>
      </span>
      <BaseSelect.ItemText>{children}</BaseSelect.ItemText>
    </BaseSelect.Item>
  );
}

interface SelectSeparatorProps extends React.HTMLAttributes<HTMLDivElement> {}

function SelectSeparator({ className, ...props }: SelectSeparatorProps) {
  return (
    <div
      role="separator"
      className={cn('bg-muted -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}

export {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectLabel,
  SelectItem,
  SelectSeparator,
};
export type {
  SelectProps,
  SelectTriggerProps,
  SelectValueProps,
  SelectContentProps,
  SelectGroupProps,
  SelectLabelProps,
  SelectItemProps,
  SelectSeparatorProps,
};
